/**
 * React Query hooks for retirement planning.
 *
 * Pattern: Follow useAllocation.ts for query key structure and 404 handling.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantProfile, useUpdateTenantProfile } from './useTenantProfile';
import { toast } from 'sonner';

import { planningApi } from '../services/planning';
import { clampSocialSecurityStartAge } from '../utils/retirementCalculations';
import type {
  RetirementInputsUpdate,
  RetirementPlanningInputs,
  RetirementNetWorth,
  RetirementEvent,
  RetirementEventCreate,
  RetirementEventUpdate,
} from '../types/retirement';

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for retirement planning.
 * Follows allocation pattern: allocationKeys.profile(), allocationKeys.targets(), etc.
 */
export const retirementKeys = {
  all: ['retirement'] as const,
  inputs: () => [...retirementKeys.all, 'inputs'] as const,
  netWorth: () => [...retirementKeys.all, 'net-worth'] as const,
  events: () => [...retirementKeys.all, 'events'] as const,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Helper to check if error is a 404.
 * Pattern from useAllocation.ts - handles both typed and untyped errors.
 */
function is404Error(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    (error as { status: number }).status === 404
  );
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch retirement planning inputs.
 * Returns null if not configured (404).
 */
export function useRetirementInputs() {
  return useQuery({
    queryKey: retirementKeys.inputs(),
    queryFn: async (): Promise<RetirementPlanningInputs | null> => {
      try {
        return await planningApi.getRetirementInputs();
      } catch (error) {
        if (is404Error(error)) {
          return null; // No inputs configured yet
        }
        throw error;
      }
    },
  });
}

/**
 * Fetch calculated net worth for retirement planning.
 * Calculates total net worth from all active accounts, excluding education accounts
 * (529 plans, ESAs) which are designated for education, not retirement.
 */
export function useRetirementNetWorth() {
  return useQuery({
    queryKey: retirementKeys.netWorth(),
    queryFn: (): Promise<RetirementNetWorth> =>
      planningApi.getRetirementNetWorth(),
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update (or create) retirement planning inputs.
 * Invalidates all retirement queries on success.
 * Shows toast on error for user feedback.
 *
 * WP-PLAN-2: every save passes through clampSocialSecurityStartAge. The API
 * rejects Social Security start ages below 62, and every caller resends the
 * whole plan - so without this, a user whose saved row predates the minimum
 * could not save any field at all. This is the single choke point for all
 * web callers (Planning page, lever panel, input panel, dashboard card).
 */
export function useUpdateRetirementInputs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RetirementInputsUpdate) =>
      planningApi.updateRetirementInputs({
        ...data,
        socialSecurityStartAge: clampSocialSecurityStartAge(
          data.socialSecurityStartAge
        ),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retirementKeys.all });
    },
    onError: (error: unknown) => {
      // Extract error message for user feedback
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'detail' in error
            ? String((error as { detail: unknown }).detail)
            : 'Failed to save retirement inputs';
      toast.error(message);
    },
  });
}

/**
 * Birth year from the tenant profile.
 *
 * WP-RETIRE-6: used by the Planning page to derive currentAge and
 * retirementAge.
 *
 * WP-PLAN-1 Phase 5: this is now a thin projection over useTenantProfile
 * rather than a second useQuery on the same key. Two independent readers of
 * ['tenant-profile', tenantId] was how the staleness bug hid - one of them
 * (FinancialPlanningSettings) wrote without invalidating, and nothing here
 * could tell. One module owns the key now; this just narrows it to the one
 * field callers want. The public shape is unchanged.
 */
export function useBirthYear() {
  const { profile, isLoading } = useTenantProfile();

  return {
    birthYear: profile?.birthYear ?? null,
    isLoading,
  };
}

/**
 * Update birth year from the Planning page's inline form.
 *
 * Goes through useUpdateTenantProfile, which owns the write and invalidates
 * ['tenant-profile', tenantId], so useBirthYear() re-reads automatically. The
 * toast lives here rather than in the shared hook because this is the only
 * caller that wants a birth-year-specific message.
 */
export function useUpdateBirthYear() {
  // WP-PLAN-1 Phase 5: delegates to the module that owns the cache key, so
  // there is one invalidation path rather than two that must agree.
  const mutation = useUpdateTenantProfile({ showToast: false });

  return {
    ...mutation,
    mutate: (birthYear: number) =>
      mutation.mutate(
        { birthYear },
        {
          onSuccess: () => toast.success('Birth year saved'),
          onError: (error: unknown) =>
            toast.error(
              error instanceof Error
                ? error.message
                : typeof error === 'object' &&
                    error !== null &&
                    'detail' in error
                  ? String((error as { detail: unknown }).detail)
                  : 'Failed to save birth year'
            ),
        }
      ),
  };
}

/**
 * Delete retirement planning inputs.
 * Invalidates all retirement queries on success.
 * Shows toast on error for user feedback.
 */
export function useDeleteRetirementInputs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => planningApi.deleteRetirementInputs(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retirementKeys.all });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'detail' in error
            ? String((error as { detail: unknown }).detail)
            : 'Failed to reset retirement inputs';
      toast.error(message);
    },
  });
}

// ============================================================================
// Retirement Event Hooks (WP-RETIRE-8)
// ============================================================================

/**
 * Fetch retirement events for the current tenant.
 * Returns empty array on 404 (no events yet).
 */
export function useRetirementEvents() {
  return useQuery({
    queryKey: retirementKeys.events(),
    queryFn: async (): Promise<RetirementEvent[]> => {
      try {
        return await planningApi.getRetirementEvents();
      } catch (error) {
        if (is404Error(error)) {
          return [];
        }
        throw error;
      }
    },
  });
}

/**
 * Create a new retirement event.
 * Invalidates events query on success.
 */
export function useCreateRetirementEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RetirementEventCreate) =>
      planningApi.createRetirementEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retirementKeys.events() });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'detail' in error
            ? String((error as { detail: unknown }).detail)
            : 'Failed to create retirement event';
      toast.error(message);
    },
  });
}

/**
 * Update an existing retirement event.
 * Invalidates events query on success.
 */
export function useUpdateRetirementEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RetirementEventUpdate }) =>
      planningApi.updateRetirementEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retirementKeys.events() });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'detail' in error
            ? String((error as { detail: unknown }).detail)
            : 'Failed to update retirement event';
      toast.error(message);
    },
  });
}

/**
 * Delete a retirement event.
 * Invalidates events query on success.
 */
export function useDeleteRetirementEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => planningApi.deleteRetirementEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retirementKeys.events() });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'detail' in error
            ? String((error as { detail: unknown }).detail)
            : 'Failed to delete retirement event';
      toast.error(message);
    },
  });
}

